import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  Tooltip,
  LabelList
} from "recharts";

/**
 * Risk Indicators Horizontal Bar Chart
 * 
 * Displays key risk dimensions:
 * - Decision Impact
 * - Human Bottlenecks
 * - Technology Dependency
 * - Change Resistance
 * - Data/Security
 */
const RiskIndicatorsChart = ({ data }) => {
  if (!data || !data.indicators) return null;

  const chartData = data.indicators.map(indicator => ({
    name: indicator.name,
    value: indicator.value,
    description: indicator.description
  }));

  // Color scale based on risk level
  const getBarColor = (value) => {
    if (value >= 70) return "#ef4444"; // Red - High
    if (value >= 45) return "#f59e0b"; // Amber - Medium
    return "#22c55e"; // Green - Low
  };

  const getRiskLabel = (level) => {
    switch(level) {
      case "elevated": return { text: "Elevated", color: "#ef4444" };
      case "moderate": return { text: "Moderate", color: "#f59e0b" };
      default: return { text: "Manageable", color: "#22c55e" };
    }
  };

  const riskLabel = getRiskLabel(data.overall_risk_level);

  return (
    <div className="w-full" data-testid="risk-indicators-chart">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-white print:text-slate-900">
            {data.title || "Risk Indicators"}
          </h3>
          <p className="text-slate-400 print:text-slate-600 text-sm">{data.subtitle}</p>
        </div>
        <div 
          className="px-3 py-1 rounded-full text-sm font-medium"
          style={{ backgroundColor: `${riskLabel.color}20`, color: riskLabel.color }}
        >
          {riskLabel.text}
        </div>
      </div>
      
      {/* Screen version */}
      <div className="h-[280px] w-full print:hidden">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            layout="vertical"
            margin={{ top: 5, right: 60, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={true} vertical={false} />
            <XAxis 
              type="number" 
              domain={[0, 100]}
              tick={{ fill: '#94a3b8', fontSize: 11 }}
              axisLine={{ stroke: '#475569' }}
            />
            <YAxis 
              type="category" 
              dataKey="name"
              tick={{ fill: '#e2e8f0', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value, name, props) => [
                `${value}/100`,
                props.payload.description
              ]}
            />
            <Bar 
              dataKey="value" 
              radius={[0, 4, 4, 0]}
              barSize={24}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
              ))}
              <LabelList 
                dataKey="value" 
                position="right" 
                fill="#94a3b8"
                fontSize={12}
                formatter={(value) => `${value}`}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Print version with fixed dimensions */}
      <div className="hidden print:block w-full" style={{ height: '250px' }}>
        <BarChart 
          width={450} 
          height={250} 
          data={chartData} 
          layout="vertical"
          margin={{ top: 5, right: 50, left: 10, bottom: 5 }}
          style={{ margin: '0 auto' }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" horizontal={true} vertical={false} />
          <XAxis 
            type="number" 
            domain={[0, 100]}
            tick={{ fill: '#374151', fontSize: 10 }}
            axisLine={{ stroke: '#9ca3af' }}
          />
          <YAxis 
            type="category" 
            dataKey="name"
            tick={{ fill: '#374151', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Bar 
            dataKey="value" 
            radius={[0, 4, 4, 0]}
            barSize={20}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.value)} />
            ))}
            <LabelList 
              dataKey="value" 
              position="right" 
              fill="#374151"
              fontSize={10}
              formatter={(value) => `${value}`}
            />
          </Bar>
        </BarChart>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-slate-400 print:text-slate-600">Low (0-44)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500"></div>
          <span className="text-slate-400 print:text-slate-600">Medium (45-69)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500"></div>
          <span className="text-slate-400 print:text-slate-600">High (70+)</span>
        </div>
      </div>
    </div>
  );
};

export default RiskIndicatorsChart;
